"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.movieService = exports.MovieService = void 0;
const firebaseBase_1 = require("./firebaseBase");
const authService_1 = require("./authService");
const dataService_1 = require("./dataService");
const movieCacheService_1 = require("./movieCacheService");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const dateUtils_1 = require("../utils/dateUtils");
const constants_1 = require("../constants");
/**
 * Movie Service (Cross-Platform)
 * Handles movie data operations like watchlist, watched status, ratings, reviews
 * Platform-agnostic version - no localStorage dependencies
 */
class MovieService {
    // =============== WATCHLIST SYNCHRONIZATION ===============
    /**
     * Sync watchlist movie data (placeholder implementation)
     */
    syncWatchlistMovieData(watchlistId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                // This is a placeholder - the actual implementation depends on the specific sync requirements
                console.log('MovieService: Syncing watchlist movie data for watchlist:', watchlistId);
                // Add actual sync logic here if needed
            }), 'syncWatchlistMovieData');
        });
    }
    // =============== WATCHLIST OPERATIONS ===============
    /**
     * Add movie to user's watchlist
     */
    addToWatchlist(user, movieId, movieTitle, moviePoster, movieYear, shareToFeed = true) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                // Get existing data to preserve watched status
                const existingData = yield this.getUserMovieData(user, movieId);
                // Enforce limit on the predefined watchlist (only when not already in it)
                if (!(existingData === null || existingData === void 0 ? void 0 : existingData.isInWatchlist)) {
                    const currentWatchlistMovies = yield this.getUserWatchlistMovies(user);
                    if (currentWatchlistMovies.length >= constants_1.APP_CONFIG.MAX_WATCHLIST_SIZE) {
                        throw new Error(`Your watchlist has reached the ${constants_1.APP_CONFIG.MAX_WATCHLIST_SIZE.toLocaleString()}-movie limit. Remove some movies to add new ones.`);
                    }
                }
                // Use the newer UserMovieData collection approach
                yield this.saveUserMovieData(user, movieId, {
                    isInWatchlist: true,
                    // Preserve existing watched status (don't default to false)
                    isWatched: existingData === null || existingData === void 0 ? void 0 : existingData.isWatched,
                    rating: existingData === null || existingData === void 0 ? void 0 : existingData.rating,
                    review: existingData === null || existingData === void 0 ? void 0 : existingData.review,
                    watchedDate: existingData === null || existingData === void 0 ? void 0 : existingData.watchedDate,
                });
                // Cache movie data in Firestore for future use (non-blocking)
                movieCacheService_1.movieCacheService.getMovieData(movieId).then(() => {
                    console.log(`💾 Movie ${movieId} cached in Firestore`);
                }).catch(err => {
                    console.warn(`Failed to cache movie ${movieId}:`, err);
                    // Don't fail the operation if caching fails
                });
                // Replace any existing activity for this movie before creating a new one
                yield this.deleteExistingMovieActivities(user, movieId);
                // Create user activity for adding to watchlist
                console.log('🎯 MovieService: About to create watchlist activity for:', { movieId, movieTitle, user: user.uid });
                try {
                    yield dataService_1.dataService.createUserActivity(user, 'movie_added_to_list', {
                        movieId,
                        movieTitle,
                        moviePoster,
                        movieYear,
                        hiddenFromCrew: !shareToFeed,
                    });
                    console.log('✅ MovieService: Successfully created watchlist activity');
                }
                catch (activityError) {
                    console.error('❌ MovieService: Failed to create watchlist activity:', activityError);
                }
                console.log('MovieService: addToWatchlist completed successfully');
            }), 'addToWatchlist');
        });
    }
    /**
     * Remove movie from user's watchlist
     */
    removeFromWatchlist(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                // Use the newer UserMovieData collection approach
                const existingData = yield this.getUserMovieData(user, movieId);
                if (existingData) {
                    if (existingData.isWatched) {
                        // Keep the data but mark as not in watchlist
                        yield this.saveUserMovieData(user, movieId, {
                            isInWatchlist: false,
                        });
                    }
                    else {
                        // Remove completely if not watched
                        const docId = `${user.uid}_${movieId}`;
                        if (isNative) {
                            try {
                                const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                                yield nativeFirestoreAdapter.deleteDocument(`userMovies/${docId}`);
                                console.log('✅ Native Firestore: Successfully deleted userMovie document');
                            }
                            catch (error) {
                                console.error('❌ Native Firestore failed in removeFromWatchlist:', error);
                                throw error;
                            }
                        }
                        else {
                            // Use unified deleteDoc for cross-platform support
                            yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)('userMovies', docId);
                        }
                    }
                }
                console.log('MovieService: removeFromWatchlist completed successfully');
            }), 'removeFromWatchlist');
        });
    }
    /**
     * Get all watchlist movies for a user
     */
    getUserWatchlistMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                if (isNative) {
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        const result = yield nativeFirestoreAdapter.getCollection('userMovies', [
                            { fieldPath: 'uid', opStr: '==', value: user.uid },
                            { fieldPath: 'isInWatchlist', opStr: '==', value: true }
                        ]);
                        return result.documents.map(doc => doc.data.movieId);
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed in getUserWatchlistMovies:', error);
                        throw error;
                    }
                }
                const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), 'userMovies'), (0, firebaseBase_1.where)('uid', '==', user.uid), (0, firebaseBase_1.where)('isInWatchlist', '==', true));
                const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                return querySnapshot.docs.map(doc => doc.data().movieId);
            }), 'getUserWatchlistMovies');
        });
    }
    // =============== WATCHED STATUS OPERATIONS ===============
    /**
     * Delete all existing watch/rate/review activities for a user+movie combination.
     * Call this before creating a new activity so the feed never shows stale duplicates.
     */
    deleteExistingMovieActivities(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const REPLACEABLE_TYPES = ['movie_watched', 'movie_reviewed', 'movie_rated', 'marked_as_watched', 'review_posted', 'movie_added_to_list', 'watchlist_add', 'added_to_watchlist'];
                const existing = yield (0, unifiedFirestoreService_1.unifiedCollection)('userActivity')
                    .where('uid', '==', user.uid)
                    .where('movieId', '==', movieId)
                    .getDocs();
                const toDelete = existing
                    .filter((doc) => REPLACEABLE_TYPES.includes(doc.data().type))
                    .map((doc) => doc.id);
                if (toDelete.length > 0) {
                    yield Promise.all(toDelete.map((id) => (0, unifiedFirestoreService_1.unifiedDeleteDoc)('userActivity', id)));
                    console.log(`🗑️ MovieService: Deleted ${toDelete.length} stale activity record(s) for movie ${movieId}`);
                }
            }
            catch (error) {
                console.warn('MovieService: Could not delete existing movie activities (non-fatal):', error);
            }
        });
    }
    /**
     * Mark movie as watched
     */
    markAsWatched(user, movieId, movieTitle, rating, review, isPrivate, moviePoster, movieYear, visibility, watchedDateOverride, watchedDatePrecision, shareToFeed = true) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('🚀 MovieService.markAsWatched called with:', {
                movieId,
                movieTitle,
                rating,
                userUid: user.uid,
                watchedDateOverride: watchedDateOverride instanceof Date ? watchedDateOverride.toISOString() : watchedDateOverride
            });
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                // Get existing data to preserve watchlist status
                const existingData = yield this.getUserMovieData(user, movieId);
                const normalizeDate = (value) => {
                    if (!value)
                        return null;
                    if (value instanceof Date) {
                        return Number.isNaN(value.getTime()) ? null : value;
                    }
                    if (typeof value === 'string') {
                        const parsed = new Date(value);
                        return Number.isNaN(parsed.getTime()) ? null : parsed;
                    }
                    if (typeof value === 'object' && value !== null && typeof value.toDate === 'function') {
                        const converted = value.toDate();
                        return Number.isNaN(converted.getTime()) ? null : converted;
                    }
                    return null;
                };
                const overrideDate = normalizeDate(watchedDateOverride);
                const existingWatchedDate = normalizeDate(existingData === null || existingData === void 0 ? void 0 : existingData.watchedDate);
                const watchedTimestamp = overrideDate
                    ? overrideDate.toISOString()
                    : existingWatchedDate
                        ? existingWatchedDate.toISOString()
                        : new Date().toISOString();
                // Use the newer UserMovieData collection approach
                yield this.saveUserMovieData(user, movieId, {
                    isInWatchlist: false,
                    isWatched: true,
                    rating: rating,
                    review: (review === null || review === void 0 ? void 0 : review.trim()) || undefined,
                    reviewVisibility: visibility || 'anyone',
                    watchedDate: watchedTimestamp,
                    watchedDatePrecision,
                });
                // Cache movie data in Firestore for future use (non-blocking)
                movieCacheService_1.movieCacheService.getMovieData(movieId).then(() => {
                    console.log(`💾 Movie ${movieId} cached in Firestore`);
                }).catch(err => {
                    console.warn(`Failed to cache movie ${movieId}:`, err);
                    // Don't fail the operation if caching fails
                });
                // Replace any existing watched/reviewed activity for this movie so the feed stays fresh
                yield this.deleteExistingMovieActivities(user, movieId);
                // Create user activity for marking as watched
                console.log('🎯 MovieService: About to create watched activity for:', { movieId, movieTitle, rating, user: user.uid });
                try {
                    yield dataService_1.dataService.createUserActivity(user, 'movie_watched', Object.assign({ movieId,
                        movieTitle,
                        moviePoster,
                        movieYear,
                        rating, review: (review === null || review === void 0 ? void 0 : review.trim()) || undefined, hiddenFromCrew: !shareToFeed, watchedDate: watchedTimestamp }, (watchedDatePrecision ? { watchedDatePrecision } : {})));
                    console.log('✅ MovieService: Successfully created watched activity');
                }
                catch (activityError) {
                    console.error('❌ MovieService: Failed to create watched activity:', activityError);
                }
                console.log('MovieService: markAsWatched completed successfully (removed from want-to-watch)');
            }), 'markAsWatched');
        });
    }
    /**
     * Unmark movie as watched
     */
    unmarkAsWatched(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                // Use the newer UserMovieData collection approach
                const existingData = yield this.getUserMovieData(user, movieId);
                if (existingData) {
                    yield this.saveUserMovieData(user, movieId, Object.assign(Object.assign({}, existingData), { isWatched: false, rating: undefined, review: undefined, reviewVisibility: undefined, watchedDate: undefined, updatedAt: new Date().toISOString() }));
                }
                console.log('MovieService: unmarkAsWatched completed successfully');
            }), 'unmarkAsWatched');
        });
    }
    /**
     * Get user's watched movies
     */
    getUserWatchedMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                if (isNative) {
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        const result = yield nativeFirestoreAdapter.getCollection('userMovies', [
                            { fieldPath: 'uid', opStr: '==', value: user.uid },
                            { fieldPath: 'isWatched', opStr: '==', value: true }
                        ]);
                        return result.documents.map(doc => doc.data);
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed in getUserWatchedMovies:', error);
                        throw error;
                    }
                }
                const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), 'userMovies'), (0, firebaseBase_1.where)('uid', '==', user.uid), (0, firebaseBase_1.where)('isWatched', '==', true));
                const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                return querySnapshot.docs.map(doc => doc.data());
            }), 'getUserWatchedMovies');
        });
    }
    /**
     * Get user's watched movies this year
     */
    getUserWatchedThisYear(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const startOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString();
                const querySnapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                    .where('uid', '==', user.uid)
                    .where('isWatched', '==', true)
                    .where('watchedDate', '>=', startOfYear)
                    .getDocs();
                return querySnapshot.map((docData) => docData.data());
            }), 'getUserWatchedThisYear');
        });
    }
    // =============== RATING AND REVIEW OPERATIONS ===============
    /**
     * Rate a movie
     */
    rateMovie(user, movieId, movieTitle, rating, moviePoster, movieYear) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e;
                yield this.saveUserMovieData(user, movieId, {
                    rating: rating,
                    updatedAt: new Date().toISOString(),
                });
                // Replace stale activity so the feed shows the updated rating
                yield this.deleteExistingMovieActivities(user, movieId);
                try {
                    const existing = yield this.getUserMovieData(user, movieId);
                    const existingWatchedDate = (existing === null || existing === void 0 ? void 0 : existing.watchedDate)
                        ? (typeof existing.watchedDate === 'string'
                            ? existing.watchedDate
                            : (_e = (_d = (_c = (_b = (_a = existing.watchedDate) === null || _a === void 0 ? void 0 : _a.toDate) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.toISOString) === null || _d === void 0 ? void 0 : _d.call(_c)) !== null && _e !== void 0 ? _e : new Date().toISOString())
                        : new Date().toISOString();
                    yield dataService_1.dataService.createUserActivity(user, 'movie_watched', Object.assign({ movieId,
                        movieTitle,
                        moviePoster,
                        movieYear,
                        rating, watchedDate: existingWatchedDate }, ((existing === null || existing === void 0 ? void 0 : existing.watchedDatePrecision) ? { watchedDatePrecision: existing.watchedDatePrecision } : {})));
                }
                catch (activityError) {
                    console.warn('MovieService: Failed to create activity after rateMovie:', activityError);
                }
                console.log('MovieService: rateMovie completed successfully');
            }), 'rateMovie');
        });
    }
    /**
     * Get movie ratings (aggregated)
     * Uses native Firestore on iOS/Android, web SDK on web
     */
    getMovieRatings(movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                if (isNative) {
                    // Use native Firestore plugin on iOS/Android
                    console.log('📱 getMovieRatings: Using NATIVE Firestore for iOS/Android');
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        // Query for all userMovies with this movieId, then filter client-side for ratings
                        // (native adapter doesn't support != operator well)
                        const result = yield nativeFirestoreAdapter.getCollection('userMovies', [{ fieldPath: 'movieId', opStr: '==', value: movieId }]);
                        return result.documents
                            .map(doc => { var _a; return (_a = doc.data) === null || _a === void 0 ? void 0 : _a.rating; })
                            .filter((rating) => typeof rating === 'number' && rating > 0)
                            .map(rating => rating > 5 ? rating / 2 : rating);
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed in getMovieRatings:', error);
                        throw error;
                    }
                }
                // Web SDK path
                const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), 'userMovies'), (0, firebaseBase_1.where)('movieId', '==', movieId), (0, firebaseBase_1.where)('rating', '!=', null));
                const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                return querySnapshot.docs
                    .map(doc => doc.data().rating)
                    .filter((rating) => typeof rating === 'number' && rating > 0)
                    .map(rating => rating > 5 ? rating / 2 : rating);
            }), 'getMovieRatings');
        });
    }
    /**
     * Get average ratings for multiple movies in a single batch
     * This is optimized for list views where we need ratings for many movies at once
     * Returns a Map of movieId -> average rating (only for movies with 2+ ratings)
     *
     * @param movieIds Array of movie IDs to fetch ratings for
     * @returns Map of movieId to average rating
     */
    getMovieRatingsBatch(movieIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!movieIds.length)
                return new Map();
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const ratingsMap = new Map();
                const ratingsByMovie = new Map();
                // Firestore 'in' operator is limited to 30 items (was 10, now 30 in recent versions)
                // We'll use 10 to be safe with older SDK versions
                const BATCH_SIZE = 10;
                // Process in batches
                for (let i = 0; i < movieIds.length; i += BATCH_SIZE) {
                    const batch = movieIds.slice(i, i + BATCH_SIZE);
                    try {
                        const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                            .where('movieId', 'in', batch)
                            .where('rating', '!=', null)
                            .getDocs();
                        snapshot.forEach((docData) => {
                            const data = docData.data();
                            const movieId = data.movieId;
                            const rating = data.rating;
                            if (typeof rating === 'number' && rating > 0) {
                                if (!ratingsByMovie.has(movieId)) {
                                    ratingsByMovie.set(movieId, []);
                                }
                                // Normalize legacy 1-10 ratings to current 0.5-5 scale
                                ratingsByMovie.get(movieId).push(rating > 5 ? rating / 2 : rating);
                            }
                        });
                    }
                    catch (error) {
                        console.warn('getMovieRatingsBatch: Batch query failed, skipping batch', { batch, error });
                        // Continue with other batches even if one fails
                    }
                }
                // Calculate averages - only for movies with 2+ ratings (matching getMovieRatings behavior)
                ratingsByMovie.forEach((ratings, movieId) => {
                    if (ratings.length > 1) {
                        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
                        ratingsMap.set(movieId, Math.round(avg * 10) / 10);
                    }
                });
                console.log(`getMovieRatingsBatch: Processed ${movieIds.length} movies, found ratings for ${ratingsMap.size} movies`);
                return ratingsMap;
            }), 'getMovieRatingsBatch');
        });
    }
    /**
     * Review a movie
     */
    reviewMovie(user, movieId, movieTitle, review, rating, isPrivate, moviePoster, movieYear) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e;
                yield this.saveUserMovieData(user, movieId, {
                    review: review.trim(),
                    rating: rating,
                    reviewVisibility: isPrivate ? 'only-me' : 'anyone',
                    movieTitle,
                    updatedAt: new Date().toISOString(),
                });
                // Replace stale activity so the feed shows the updated review/rating
                yield this.deleteExistingMovieActivities(user, movieId);
                // Track review for feed unless private
                if (!isPrivate) {
                    try {
                        const existingForReview = yield this.getUserMovieData(user, movieId);
                        const reviewWatchedDate = (existingForReview === null || existingForReview === void 0 ? void 0 : existingForReview.watchedDate)
                            ? (typeof existingForReview.watchedDate === 'string'
                                ? existingForReview.watchedDate
                                : (_e = (_d = (_c = (_b = (_a = existingForReview.watchedDate) === null || _a === void 0 ? void 0 : _a.toDate) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.toISOString) === null || _d === void 0 ? void 0 : _d.call(_c)) !== null && _e !== void 0 ? _e : new Date().toISOString())
                            : new Date().toISOString();
                        yield dataService_1.dataService.createUserActivity(user, 'movie_reviewed', Object.assign({ movieId,
                            movieTitle,
                            moviePoster,
                            rating, review: review.trim(), watchedDate: reviewWatchedDate }, ((existingForReview === null || existingForReview === void 0 ? void 0 : existingForReview.watchedDatePrecision) ? { watchedDatePrecision: existingForReview.watchedDatePrecision } : {})));
                        console.log('✅ MovieService: Successfully created reviewed activity');
                    }
                    catch (activityError) {
                        console.error('❌ MovieService: Failed to create reviewed activity:', activityError);
                    }
                }
                console.log('MovieService: reviewMovie completed successfully');
            }), 'reviewMovie');
        });
    }
    /**
     * Update movie review
     */
    updateMovieReview(user, movieId, movieTitle, review, rating, isPrivate = false, moviePoster, movieYear) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e;
                yield this.saveUserMovieData(user, movieId, {
                    review: review.trim(),
                    rating: rating,
                    reviewVisibility: isPrivate ? 'only-me' : 'anyone',
                    movieTitle,
                    updatedAt: new Date().toISOString(),
                });
                // Replace stale activity so the feed shows the updated review/rating
                yield this.deleteExistingMovieActivities(user, movieId);
                if (!isPrivate) {
                    try {
                        const existingForUpdate = yield this.getUserMovieData(user, movieId);
                        const updateWatchedDate = (existingForUpdate === null || existingForUpdate === void 0 ? void 0 : existingForUpdate.watchedDate)
                            ? (typeof existingForUpdate.watchedDate === 'string'
                                ? existingForUpdate.watchedDate
                                : (_e = (_d = (_c = (_b = (_a = existingForUpdate.watchedDate) === null || _a === void 0 ? void 0 : _a.toDate) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.toISOString) === null || _d === void 0 ? void 0 : _d.call(_c)) !== null && _e !== void 0 ? _e : new Date().toISOString())
                            : new Date().toISOString();
                        yield dataService_1.dataService.createUserActivity(user, 'movie_reviewed', Object.assign({ movieId,
                            movieTitle,
                            moviePoster,
                            movieYear,
                            rating, review: review.trim(), watchedDate: updateWatchedDate }, ((existingForUpdate === null || existingForUpdate === void 0 ? void 0 : existingForUpdate.watchedDatePrecision) ? { watchedDatePrecision: existingForUpdate.watchedDatePrecision } : {})));
                    }
                    catch (activityError) {
                        console.warn('MovieService: Failed to create activity after updateMovieReview:', activityError);
                    }
                }
                console.log('MovieService: updateMovieReview completed successfully');
                return 'Review updated successfully';
            }), 'updateMovieReview');
        });
    }
    /**
     * Delete movie rating and review
     */
    deleteMovieRatingAndReview(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docId = `${user.uid}_${movieId}`;
                // Use unifiedSetDoc (merge:true) to patch only the fields we want cleared.
                // Avoids the read-modify-write in saveUserMovieData which can race or
                // silently skip when the doc doesn't exist yet in the read phase.
                // null is used instead of deleteField() because the Capacitor native
                // plugin does not support Firestore sentinel values.
                yield (0, unifiedFirestoreService_1.unifiedSetDoc)('userMovies', docId, {
                    uid: user.uid,
                    movieId,
                    isWatched: false,
                    watchCount: 0,
                    rating: null,
                    review: null,
                    reviewVisibility: null,
                    watchedDate: null,
                    lastWatchedDate: null,
                    watchedDatePrecision: null,
                    updatedAt: new Date().toISOString(),
                }, true /* merge */);
                // Remove crew-feed activity records so the post disappears from the feed
                yield this.deleteExistingMovieActivities(user, movieId);
                console.log('MovieService: deleteMovieRatingAndReview completed successfully');
            }), 'deleteMovieRatingAndReview');
        });
    }
    /**
     * Get movie reviews (public)
     * Uses native Firestore on iOS/Android, web SDK on web
     */
    getMovieReviews(movieId, viewerUserId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e, _f;
                console.log('🔍 MovieService: Fetching reviews for movieId:', movieId, 'viewerUserId:', viewerUserId);
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                console.log('📱 getMovieReviews platform detection:', { isNative, platform: (_d = (_c = window.Capacitor) === null || _c === void 0 ? void 0 : _c.getPlatform) === null || _d === void 0 ? void 0 : _d.call(_c) });
                let userMovieDocs = [];
                if (isNative) {
                    // Use native Firestore plugin on iOS/Android
                    console.log('📱 getMovieReviews: Using NATIVE Firestore for iOS/Android');
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        const result = yield nativeFirestoreAdapter.getCollection('userMovies', [{ fieldPath: 'movieId', opStr: '==', value: movieId }]);
                        userMovieDocs = result.documents.map(doc => ({
                            id: doc.id,
                            data: doc.data
                        }));
                        console.log('✅ Native Firestore: Got', userMovieDocs.length, 'user movie records for reviews');
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed in getMovieReviews:', error);
                        throw error;
                    }
                }
                else {
                    // Use web Firestore SDK on web browsers
                    console.log('🌐 getMovieReviews: Using WEB Firestore SDK');
                    const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), 'userMovies'), (0, firebaseBase_1.where)('movieId', '==', movieId));
                    const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                    userMovieDocs = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: doc.data()
                    }));
                    console.log('📊 MovieService: Found', userMovieDocs.length, 'total user movie records for this movie');
                }
                const reviews = [];
                for (const userMovieDoc of userMovieDocs) {
                    const data = userMovieDoc.data;
                    console.log('📝 MovieService: Checking document', userMovieDoc.id, {
                        userId: data.uid,
                        hasReview: !!data.review,
                        reviewLength: ((_e = data.review) === null || _e === void 0 ? void 0 : _e.length) || 0,
                        reviewVisibility: data.reviewVisibility,
                        isWatched: data.isWatched
                    });
                    // Only include documents that have actual reviews
                    if (!data.review || !data.review.trim()) {
                        console.log('❌ Skipping - no review content');
                        continue;
                    }
                    // Skip private reviews unless it's the viewer's own review
                    if (data.reviewVisibility === 'only-me' && data.uid !== viewerUserId) {
                        console.log('🔒 Skipping - private review from different user');
                        continue;
                    }
                    // Fetch user profile for this review
                    let userName = 'User';
                    let userAvatar;
                    try {
                        const userProfile = yield authService_1.authService.getUserProfile(data.uid);
                        if (userProfile) {
                            userName = userProfile.displayName || userProfile.username || `User ${data.uid.slice(-4)}`;
                            userAvatar = userProfile.photoURL;
                            // Skip reviews from private profiles unless the viewer is an approved follower
                            if (((_f = userProfile.privacy) === null || _f === void 0 ? void 0 : _f.profileVisibility) === 'private' && data.uid !== viewerUserId) {
                                if (!viewerUserId)
                                    continue;
                                const followDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userFollowing', `${viewerUserId}_${data.uid}`);
                                if (!followDoc.exists())
                                    continue;
                            }
                        }
                    }
                    catch (error) {
                        console.warn(`Failed to fetch user profile for review by ${data.uid}:`, error);
                        // Use fallback name if profile fetch fails
                        userName = `User ${data.uid.slice(-4)}`;
                    }
                    const toDate = (value) => {
                        if (!value)
                            return null;
                        if (value instanceof Date) {
                            return Number.isNaN(value.getTime()) ? null : value;
                        }
                        if (value instanceof firebaseBase_1.Timestamp) {
                            return value.toDate();
                        }
                        if (typeof value === 'object' && value !== null && typeof value.toDate === 'function') {
                            const converted = value.toDate();
                            return Number.isNaN(converted.getTime()) ? null : converted;
                        }
                        if (typeof value === 'string') {
                            const parsed = new Date(value);
                            return Number.isNaN(parsed.getTime()) ? null : parsed;
                        }
                        return null;
                    };
                    const resolvedWatchedDate = toDate(data.watchedDate) ||
                        toDate(data.lastWatchedDate);
                    // Do NOT fall back to watchedDate — that's a different field with different semantics.
                    // If updatedAt/createdAt are missing, leave updatedDate undefined so the UI shows nothing
                    // rather than incorrectly displaying the watch date as the review-update date.
                    const resolvedUpdatedDate = toDate(data.updatedAt) ||
                        toDate(data.lastUpdated) ||
                        toDate(data.createdAt);
                    const watchedDateLabel = (0, dateUtils_1.formatWatchedDate)(resolvedWatchedDate, data.watchedDatePrecision) || undefined;
                    const watchedDateIso = resolvedWatchedDate ? resolvedWatchedDate.toISOString() : undefined;
                    const updatedDateLabel = resolvedUpdatedDate
                        ? (0, dateUtils_1.formatFirebaseDate)(resolvedUpdatedDate, 'Recently')
                        : undefined;
                    const updatedDateIso = resolvedUpdatedDate ? resolvedUpdatedDate.toISOString() : undefined;
                    console.log(`[getMovieReviews] date resolution for ${userMovieDoc.id}:`, {
                        raw_updatedAt: data.updatedAt,
                        raw_createdAt: data.createdAt,
                        raw_watchedDate: data.watchedDate,
                        resolvedUpdatedDate: resolvedUpdatedDate === null || resolvedUpdatedDate === void 0 ? void 0 : resolvedUpdatedDate.toISOString(),
                        resolvedWatchedDate: resolvedWatchedDate === null || resolvedWatchedDate === void 0 ? void 0 : resolvedWatchedDate.toISOString(),
                        updatedDateIso,
                        watchedDateIso,
                    });
                    const reviewData = {
                        id: userMovieDoc.id,
                        userId: data.uid,
                        userName: userName,
                        userAvatar: userAvatar,
                        rating: data.rating,
                        review: data.review,
                        reviewVisibility: data.reviewVisibility,
                        date: updatedDateLabel || watchedDateLabel || 'Recently',
                        watchedDate: watchedDateIso,
                        watchedDateLabel,
                        watchedDatePrecision: data.watchedDatePrecision,
                        updatedDate: updatedDateIso,
                        updatedDateLabel,
                        likes: data.reviewLikes || 0,
                        likedBy: data.reviewLikedBy || [],
                        comments: data.reviewCommentCount || 0
                    };
                    reviews.push(reviewData);
                    console.log('✅ Added review from user:', userName, 'Rating:', data.rating, 'Visibility:', data.reviewVisibility, 'Likes:', reviewData.likes);
                }
                console.log('📋 MovieService: Returning', reviews.length, 'reviews for movieId:', movieId);
                return reviews;
            }), 'getMovieReviews');
        });
    }
    /**
     * Toggle like on a review (Legacy client-side method)
     * @deprecated Use atomic Functions API instead - kept as fallback only
     */
    toggleReviewLikeLegacy(reviewId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const reviewDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', reviewId);
                if (!reviewDoc.exists()) {
                    throw new Error('Review not found');
                }
                const reviewData = reviewDoc.data();
                const likedBy = (reviewData.reviewLikedBy || []);
                const isLiked = likedBy.includes(userId);
                const reviewAuthorId = reviewData.uid; // The author of the review
                const now = new Date().toISOString();
                if (isLiked) {
                    // Unlike: remove user from likedBy array and decrement count
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userMovies', reviewId, {
                        reviewLikedBy: likedBy.filter(uid => uid !== userId),
                        reviewLikes: Math.max(0, (reviewData.reviewLikes || 0) - 1),
                        updatedAt: now
                    });
                    // Update the review author's likesReceived count
                    if (reviewAuthorId) {
                        const authorDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userProfiles', reviewAuthorId);
                        if (authorDoc.exists()) {
                            const authorData = authorDoc.data();
                            const currentLikesReceived = ((_a = authorData.stats) === null || _a === void 0 ? void 0 : _a.likesReceived) || 0;
                            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userProfiles', reviewAuthorId, {
                                'stats.likesReceived': Math.max(0, currentLikesReceived - 1),
                                updatedAt: now
                            });
                        }
                    }
                }
                else {
                    // Like: add user to likedBy array (manual array operation for platform compatibility)
                    const updatedLikedBy = [...likedBy, userId];
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userMovies', reviewId, {
                        reviewLikedBy: updatedLikedBy,
                        reviewLikes: (reviewData.reviewLikes || 0) + 1,
                        updatedAt: now
                    });
                    // Update the review author's likesReceived count
                    if (reviewAuthorId) {
                        const authorDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userProfiles', reviewAuthorId);
                        if (authorDoc.exists()) {
                            const authorData = authorDoc.data();
                            const currentLikesReceived = ((_b = authorData.stats) === null || _b === void 0 ? void 0 : _b.likesReceived) || 0;
                            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userProfiles', reviewAuthorId, {
                                'stats.likesReceived': currentLikesReceived + 1,
                                updatedAt: now
                            });
                            // Notification is handled by the onReviewLiked Firestore trigger in functions/src/index.ts
                        }
                    }
                }
                console.log('MovieService: toggleReviewLikeLegacy completed successfully');
            }), 'toggleReviewLikeLegacy');
        });
    }
    /**
     * Toggle like on a review
     * Uses legacy client-side method by default
     * Note: Web/mobile apps should use Functions API directly via functionsApi.likeReview()
     */
    toggleReviewLike(reviewId, userId) {
        return __awaiter(this, void 0, void 0, function* () {
            // Use legacy method - web/mobile apps should use functionsApi.likeReview() instead
            return this.toggleReviewLikeLegacy(reviewId, userId);
        });
    }
    // =============== USER MOVIE DATA OPERATIONS ===============
    /**
     * Save user movie data
     */
    saveUserMovieData(user, movieId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docId = `${user.uid}_${movieId}`;
                const existingDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', docId);
                const existingData = existingDoc.exists() ? existingDoc.data() : null;
                const now = new Date().toISOString();
                const movieData = Object.assign(Object.assign(Object.assign({ uid: user.uid, movieId: movieId, isInWatchlist: false, isWatched: false, isFavorite: false, watchCount: 0 }, existingData), data), { createdAt: (existingData === null || existingData === void 0 ? void 0 : existingData.createdAt) || now, updatedAt: now });
                // Filter out undefined values to prevent Firestore errors
                const cleanedData = Object.fromEntries(Object.entries(movieData).filter(([_, value]) => value !== undefined));
                yield (0, unifiedFirestoreService_1.unifiedSetDoc)('userMovies', docId, cleanedData);
            }), 'saveUserMovieData');
        });
    }
    /**
     * Get user movie data
     */
    getUserMovieData(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docId = `${user.uid}_${movieId}`;
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', docId);
                return docSnap.exists() ? docSnap.data() : null;
            }), 'getUserMovieData');
        });
    }
    /**
     * Delete user movie data
     */
    deleteUserMovieData(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                const docId = `${user.uid}_${movieId}`;
                if (isNative) {
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        yield nativeFirestoreAdapter.deleteDocument(`userMovies/${docId}`);
                        console.log('✅ Native Firestore: Successfully deleted userMovie data');
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed in deleteUserMovieData:', error);
                        throw error;
                    }
                }
                else {
                    // Use unified deleteDoc for cross-platform support
                    yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)('userMovies', docId);
                }
            }), 'deleteUserMovieData');
        });
    }
    /**
     * Get all user movie data
     * Uses native Firestore on iOS/Android, web SDK on web
     */
    getAllUserMovieData(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d;
                console.log('🔍 MovieService.getAllUserMovieData: Starting query for user:', user.uid);
                // Detect if running on native platform
                const isNative = typeof window.Capacitor !== 'undefined' && ((_b = (_a = window.Capacitor).isNativePlatform) === null || _b === void 0 ? void 0 : _b.call(_a));
                console.log('📱 Platform detection:', { isNative, platform: (_d = (_c = window.Capacitor) === null || _c === void 0 ? void 0 : _c.getPlatform) === null || _d === void 0 ? void 0 : _d.call(_c) });
                if (isNative) {
                    // Use native Firestore plugin on iOS/Android
                    console.log('📱 Using NATIVE Firestore for iOS/Android');
                    try {
                        const { nativeFirestoreAdapter } = yield Promise.resolve().then(() => __importStar(require('./nativeFirestoreAdapter')));
                        const result = yield nativeFirestoreAdapter.getCollection('userMovies', [{ fieldPath: 'uid', opStr: '==', value: user.uid }]);
                        console.log('✅ Native Firestore: Got', result.size, 'documents');
                        return result.documents.map(doc => doc.data);
                    }
                    catch (error) {
                        console.error('❌ Native Firestore failed:', error);
                        throw error;
                    }
                }
                else {
                    // Use web Firestore SDK on web browsers
                    console.log('🌐 Using WEB Firestore SDK');
                    const q = (0, firebaseBase_1.query)((0, firebaseBase_1.collection)((0, firebaseBase_1.getDb)(), 'userMovies'), (0, firebaseBase_1.where)('uid', '==', user.uid));
                    const querySnapshot = yield (0, firebaseBase_1.getDocs)(q);
                    console.log('✅ Web Firestore: Got', querySnapshot.docs.length, 'documents');
                    return querySnapshot.docs.map(doc => doc.data());
                }
            }), 'getAllUserMovieData');
        });
    }
    // =============== UTILITY METHODS ===============
    /**
     * Get movie state for display
     */
    getMovieStateForDisplay(movieData) {
        if (!movieData)
            return null;
        return {
            movieId: movieData.movieId,
            isInWatchlist: movieData.isInWatchlist,
            isWatched: movieData.isWatched,
            rating: movieData.rating,
            review: movieData.review,
            watchedDate: movieData.watchedDate ?
                (movieData.watchedDate instanceof firebaseBase_1.Timestamp ?
                    movieData.watchedDate.toDate().toISOString() :
                    new Date().toISOString()) : undefined,
            addedToWatchlistDate: movieData.createdAt ?
                (movieData.createdAt instanceof firebaseBase_1.Timestamp ?
                    movieData.createdAt.toDate().toISOString() :
                    new Date().toISOString()) : undefined,
            updatedAt: movieData.updatedAt ?
                (movieData.updatedAt instanceof firebaseBase_1.Timestamp ?
                    movieData.updatedAt.toDate().toISOString() :
                    new Date().toISOString()) : new Date().toISOString(),
        };
    }
    /**
     * Format movie date
     */
    formatMovieDate(dateString) {
        return (0, dateUtils_1.formatFirebaseDate)(dateString, '', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }
    /**
     * Get movie status text
     */
    getMovieStatusText(movieState) {
        if (movieState.isWatched) {
            return movieState.rating ? `Watched • Rated ${movieState.rating}/5` : 'Watched';
        }
        if (movieState.isInWatchlist) {
            return 'Want to Watch';
        }
        return 'Not in List';
    }
    // =============== OTHER USER DATA (PUBLIC) ===============
    /**
     * Get other user's movie data (public only)
     */
    getOtherUserMovieData(userId, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docId = `${userId}_${movieId}`;
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', docId);
                return docSnap.exists() ? docSnap.data() : null;
            }), 'getOtherUserMovieData');
        });
    }
    /**
     * Get other user's movie data for multiple movies in a batch
     * This is optimized for loading other users' watchlists efficiently
     *
     * @param userId The user whose movies to fetch
     * @param movieIds Array of movie IDs to fetch
     * @returns Map of movieId to UserMovieData
     */
    getOtherUserMovieDataBatch(userId, movieIds) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!movieIds.length)
                return new Map();
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const movieDataMap = new Map();
                // Firestore 'in' operator is limited to 10 items in older SDKs
                const BATCH_SIZE = 10;
                // Process in batches
                for (let i = 0; i < movieIds.length; i += BATCH_SIZE) {
                    const batch = movieIds.slice(i, i + BATCH_SIZE);
                    try {
                        const snapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                            .where('uid', '==', userId)
                            .where('movieId', 'in', batch)
                            .getDocs();
                        snapshot.forEach((docData) => {
                            const data = docData.data();
                            movieDataMap.set(data.movieId, data);
                        });
                    }
                    catch (error) {
                        console.warn('getOtherUserMovieDataBatch: Batch query failed, skipping batch', { batch, error });
                        // Continue with other batches even if one fails
                    }
                }
                console.log(`getOtherUserMovieDataBatch: Fetched ${movieDataMap.size} of ${movieIds.length} requested movies for user ${userId}`);
                return movieDataMap;
            }), 'getOtherUserMovieDataBatch');
        });
    }
    /**
     * Get other user's watchlist movies
     */
    getOtherUserWatchlistMovies(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const querySnapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                    .where('uid', '==', userId)
                    .where('isInWatchlist', '==', true)
                    .getDocs();
                return querySnapshot.map((docData) => docData.data().movieId);
            }), 'getOtherUserWatchlistMovies');
        });
    }
    /**
     * Get other user's watched movies
     */
    getOtherUserWatchedMovies(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const querySnapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                    .where('uid', '==', userId)
                    .where('isWatched', '==', true)
                    .getDocs();
                return querySnapshot.map((docData) => docData.data());
            }), 'getOtherUserWatchedMovies');
        });
    }
    /**
     * Get other user's all movie data
     * Uses UnifiedFirestore to work on both web and native iOS/Android
     */
    getOtherUserAllMovieData(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                console.log('🎬 MovieService.getOtherUserAllMovieData: Fetching movies for user:', userId);
                const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)('userMovies')
                    .where('uid', '==', userId)
                    .getDocs();
                console.log(`✅ MovieService.getOtherUserAllMovieData: Got ${docs.length} movies`);
                return docs.map(doc => doc.data());
            }), 'getOtherUserAllMovieData');
        });
    }
    // =============== ACTIVITY BACKFILL UTILITIES ===============
    /**
     * Create activities for existing watched movies (backfill utility)
     */
    backfillWatchedActivities(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                console.log('🔄 MovieService: Starting watched activities backfill for user:', user.uid);
                // Get all watched movies for the user
                const watchedMovies = yield this.getUserWatchedMovies(user);
                console.log(`📊 MovieService: Found ${watchedMovies.length} watched movies to backfill`);
                let createdCount = 0;
                let skippedCount = 0;
                for (const movieData of watchedMovies) {
                    try {
                        // Check if activity already exists for this movie
                        const existingActivities = yield dataService_1.dataService.getUserActivities(user, 100);
                        const hasActivity = existingActivities.some(activity => activity.type === 'movie_watched' && activity.movieId === movieData.movieId);
                        if (hasActivity) {
                            console.log(`⏭️ MovieService: Activity already exists for movie ${movieData.movieId}, skipping`);
                            skippedCount++;
                            continue;
                        }
                        // Create activity for this watched movie
                        yield dataService_1.dataService.createUserActivity(user, 'movie_watched', {
                            movieId: movieData.movieId,
                            movieTitle: `Movie ${movieData.movieId}`,
                            moviePoster: undefined,
                            rating: movieData.rating
                        });
                        createdCount++;
                        console.log(`✅ MovieService: Created backfill activity for movie ${movieData.movieId}`);
                        // Add small delay to avoid overwhelming Firebase
                        yield new Promise(resolve => setTimeout(resolve, 100));
                    }
                    catch (error) {
                        console.error(`❌ MovieService: Failed to create backfill activity for movie ${movieData.movieId}:`, error);
                    }
                }
                console.log(`🎉 MovieService: Backfill completed. Created: ${createdCount}, Skipped: ${skippedCount}, Total: ${watchedMovies.length}`);
            }), 'backfillWatchedActivities');
        });
    }
    /**
     * Get shareable review data (only public reviews)
     */
    getShareableReview(reviewId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                console.log(`🔍 MovieService: Fetching shareable review for ID: ${reviewId}`);
                const reviewSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', reviewId);
                if (!reviewSnap.exists()) {
                    console.warn(`❌ MovieService: Review ${reviewId} not found`);
                    return null;
                }
                const reviewData = reviewSnap.data();
                // Only allow sharing public reviews
                if (reviewData.reviewVisibility === 'only-me') {
                    console.warn(`🔒 MovieService: Cannot share private review ${reviewId}`);
                    return null;
                }
                if (!reviewData.review) {
                    console.warn(`📝 MovieService: Review ${reviewId} has no review text`);
                    return null;
                }
                // Fetch user profile
                let userName = 'User';
                let userAvatar;
                try {
                    const userProfile = yield authService_1.authService.getUserProfile(reviewData.uid);
                    if (userProfile) {
                        userName = userProfile.displayName || userProfile.username || `User ${reviewData.uid.slice(-4)}`;
                        userAvatar = userProfile.photoURL;
                    }
                }
                catch (error) {
                    console.warn(`Failed to fetch user profile for shareable review:`, error);
                }
                return Object.assign(Object.assign({}, reviewData), { id: reviewSnap.id, userName,
                    userAvatar });
            }), 'getShareableReview');
        });
    }
    /**
     * Track review share event
     */
    trackReviewShare(reviewId, userId, platform) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const now = new Date().toISOString();
                    // Increment share count in userMovies
                    const reviewSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userMovies', reviewId);
                    if (reviewSnap.exists()) {
                        const currentData = reviewSnap.data();
                        const currentShares = currentData.shareCount || 0;
                        yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userMovies', reviewId, {
                            shareCount: currentShares + 1,
                            lastSharedAt: now,
                            updatedAt: now
                        });
                    }
                    // Track share event in a separate collection
                    yield (0, unifiedFirestoreService_1.unifiedAddDoc)('reviewShares', {
                        reviewId,
                        userId,
                        sharedAt: now,
                        platform: platform || 'unknown',
                    });
                    console.log(`✅ MovieService: Review ${reviewId} shared by ${userId} on ${platform}`);
                }
                catch (error) {
                    console.error('Error tracking review share:', error);
                }
            }), 'trackReviewShare');
        });
    }
}
exports.MovieService = MovieService;
// Export singleton instance
exports.movieService = new MovieService();
exports.default = exports.movieService;
